import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth, db } from '../config/firebase';
import { normalizeWorkspaceCustomDomain } from '../utils/sheetDomains';
import {
  DEFAULT_WORKSPACE_PRIMARY_COLOR,
  normalizeWorkspacePrimaryColor
} from '../utils/sheetWorkspaceTheme';
import { sanitizeWorkspaceDashboardConfig } from '../utils/workspaceDashboard';

const WORKSPACES_COLLECTION = 'docs-sheet-workspaces';
const WORKSPACE_MEMBERS_SUBCOLLECTION = 'members';
const WORKSPACE_SLUGS_COLLECTION = 'docs-sheet-workspace-slugs';
const WORKSPACE_DOMAINS_COLLECTION = 'docs-sheet-workspace-domains';
const USERS_COLLECTION = 'parrotdocs-users';
const functions = getFunctions(app, 'us-central1');

const normalizeWorkspaceVisibility = (value) => (
  value === 'restricted' ? 'restricted' : 'private'
);

const sanitizeTemplateFlag = (value) => value === true;

const normalizeWorkspaceAccess = (value) => (
  value === 'edit' || value === 'edit_own_rows' ? value : 'view'
);
const normalizeAdminBuilderAccessEnabled = (value) => value === true;

const normalizeWorkspaceRole = (value) => (
  ['owner', 'admin', 'editor', 'contributor', 'viewer'].includes(`${value || ''}`.trim())
    ? `${value}`.trim()
    : 'viewer'
);

const normalizeWorkspaceSlug = (value, fallback = 'workspace') => {
  const normalized = `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 64);
  return normalized || fallback;
};

const normalizeStringList = (values = []) => Array.from(new Set(
  (Array.isArray(values) ? values : [])
    .map((item) => `${item || ''}`.trim())
    .filter(Boolean)
));

const normalizeWorkspaceAiConfig = (value = {}, fallbackEnabled = false) => {
  const source = value && typeof value === 'object' ? value : {};
  return stripUndefinedDeep({
    enabled: source.enabled === true || fallbackEnabled === true,
    provider: source.provider === 'openai' ? 'openai' : 'gemini',
    skills: normalizeStringList(source.skills),
    instructions: `${source.instructions || ''}`.trim() || undefined
  });
};

const normalizeWorkspaceAgentConfig = (value = {}, fallbackEnabled = false) => {
  const source = value && typeof value === 'object' ? value : {};
  return stripUndefinedDeep({
    enabled: source.enabled === true || fallbackEnabled === true,
    defaultAgentName: `${source.defaultAgentName || ''}`.trim() || undefined,
    automationLevel: ['manual', 'suggest', 'auto'].includes(`${source.automationLevel || ''}`.trim())
      ? `${source.automationLevel}`.trim()
      : 'manual',
    skills: normalizeStringList(source.skills),
    instructions: `${source.instructions || ''}`.trim() || undefined
  });
};

const stripUndefinedDeep = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedDeep(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === 'object') {
    if (value.constructor !== Object) return value;
    const output = {};
    Object.entries(value).forEach(([key, item]) => {
      const cleaned = stripUndefinedDeep(item);
      if (cleaned !== undefined) output[key] = cleaned;
    });
    return output;
  }

  return value === undefined ? undefined : value;
};

const sortByUpdatedAtDesc = (items = []) => [...items].sort((left, right) => {
  const leftTime = left?.updatedAt?.toMillis?.()
    || left?.createdAt?.toMillis?.()
    || new Date(left?.updatedAt || left?.createdAt || 0).getTime();
  const rightTime = right?.updatedAt?.toMillis?.()
    || right?.createdAt?.toMillis?.()
    || new Date(right?.updatedAt || right?.createdAt || 0).getTime();
  return rightTime - leftTime;
});

const getWorkspaceRef = (workspaceId) => doc(db, WORKSPACES_COLLECTION, workspaceId);
const getMemberRef = (workspaceId, userId) => doc(db, WORKSPACES_COLLECTION, workspaceId, WORKSPACE_MEMBERS_SUBCOLLECTION, userId);
const getMembersCollectionRef = (workspaceId) => collection(db, WORKSPACES_COLLECTION, workspaceId, WORKSPACE_MEMBERS_SUBCOLLECTION);
const getSlugRef = (slug) => doc(db, WORKSPACE_SLUGS_COLLECTION, slug);
const getDomainRef = (domain) => doc(db, WORKSPACE_DOMAINS_COLLECTION, normalizeWorkspaceCustomDomain(domain));

const assertNotWorkspaceOwnerMember = async (workspaceId, memberUserId) => {
  const workspaceSnap = await getDoc(getWorkspaceRef(workspaceId));
  if (!workspaceSnap.exists()) throw new Error('Workspace not found');

  const workspaceOwnerId = `${workspaceSnap.data()?.userId || ''}`.trim();
  if (workspaceOwnerId && workspaceOwnerId === `${memberUserId || ''}`.trim()) {
    throw new Error('The workspace owner membership cannot be changed or removed.');
  }
};

const normalizeWorkspace = (docSnap) => ({
  id: docSnap.id,
  ...docSnap.data(),
  customDomain: normalizeWorkspaceCustomDomain(docSnap.data()?.customDomain)
});

const ensureUniqueWorkspaceSlug = async (preferredValue, excludedWorkspaceId = '') => {
  const baseSlug = normalizeWorkspaceSlug(preferredValue);

  for (let attempt = 0; attempt < 50; attempt += 1) {
    const candidate = attempt === 0 ? baseSlug : `${baseSlug}-${attempt + 1}`;
    const slugSnap = await getDoc(getSlugRef(candidate));
    if (!slugSnap.exists()) return candidate;
    if (`${slugSnap.data()?.workspaceId || ''}`.trim() === `${excludedWorkspaceId || ''}`.trim()) {
      return candidate;
    }
  }

  throw new Error('Could not reserve a unique workspace URL path. Try a different name.');
};

const ensureUniqueWorkspaceCustomDomain = async (preferredValue, excludedWorkspaceId = '') => {
  const normalized = normalizeWorkspaceCustomDomain(preferredValue);
  if (!normalized) return '';

  const domainSnap = await getDoc(getDomainRef(normalized));
  if (!domainSnap.exists()) return normalized;
  if (`${domainSnap.data()?.workspaceId || ''}`.trim() === `${excludedWorkspaceId || ''}`.trim()) {
    return normalized;
  }

  throw new Error('That custom domain is already connected to another workspace.');
};

const isPermissionDeniedError = (error) => (
  error?.code === 'permission-denied' ||
  `${error?.message || ''}`.toLowerCase().includes('missing or insufficient permissions')
);

const withWorkspaceUpdateContext = (step, error) => {
  if (isPermissionDeniedError(error)) {
    return new Error(`Permission denied while ${step}.`);
  }
  return error;
};

export const sheetWorkspaceService = {
  async getAiCredentialStatus(workspaceId) {
    const callable = httpsCallable(functions, 'getWorkspaceAiCredentialStatus', { timeout: 30000 });
    const result = await callable({ workspaceId });
    return result.data || { gemini: false, openai: false };
  },

  async setAiCredential(workspaceId, provider, apiKey) {
    const callable = httpsCallable(functions, 'setWorkspaceAiCredential', { timeout: 30000 });
    const result = await callable({ workspaceId, provider, apiKey });
    return result.data || {};
  },

  async removeAiCredential(workspaceId, provider) {
    const callable = httpsCallable(functions, 'removeWorkspaceAiCredential', { timeout: 30000 });
    const result = await callable({ workspaceId, provider });
    return result.data || {};
  },

  async createWorkspace(workspaceData = {}, userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to create a workspace');

    const workspaceRef = workspaceData.documentId
      ? getWorkspaceRef(workspaceData.documentId)
      : doc(collection(db, WORKSPACES_COLLECTION));
    const workspaceId = workspaceRef.id;
    const title = `${workspaceData.title || ''}`.trim();
    const slug = await ensureUniqueWorkspaceSlug(workspaceData.slug || title || workspaceId.slice(0, 8));
    const customDomain = await ensureUniqueWorkspaceCustomDomain(workspaceData.customDomain, workspaceId);

    if (!title) {
      throw new Error('Workspace title is required');
    }

    const payload = stripUndefinedDeep({
      documentId: workspaceId,
      title,
      slug,
      customDomain: customDomain || undefined,
      description: `${workspaceData.description || ''}`.trim(),
      organizationIds: normalizeStringList(workspaceData.organizationIds),
      userId: ownerId,
      visibility: normalizeWorkspaceVisibility(workspaceData.visibility),
      defaultCollaboratorAccess: normalizeWorkspaceAccess(workspaceData.defaultCollaboratorAccess),
      allowCrossSheetReferences: workspaceData.allowCrossSheetReferences !== false,
      adminBuilderAccessEnabled: normalizeAdminBuilderAccessEnabled(workspaceData.adminBuilderAccessEnabled),
      primaryColor: normalizeWorkspacePrimaryColor(workspaceData.primaryColor) || DEFAULT_WORKSPACE_PRIMARY_COLOR,
      defaultLogSheetId: `${workspaceData.defaultLogSheetId || ''}`.trim() || undefined,
      aiConfig: normalizeWorkspaceAiConfig(workspaceData.aiConfig, workspaceData.aiConfig?.enabled),
      agentConfig: normalizeWorkspaceAgentConfig(workspaceData.agentConfig, workspaceData.agentConfig?.enabled),
      dashboardConfig: sanitizeWorkspaceDashboardConfig(workspaceData.dashboardConfig, [], { seedLegacy: false }),
      isTemplate: sanitizeTemplateFlag(workspaceData.isTemplate),
      templateClonedFromId: `${workspaceData.templateClonedFromId || ''}`.trim() || undefined,
      sheetCount: Math.max(0, Number(workspaceData.sheetCount) || 0),
      updatedAt: serverTimestamp()
    });

    await runTransaction(db, async (transaction) => {
      const slugRef = getSlugRef(slug);
      const slugSnap = await transaction.get(slugRef);
      if (slugSnap.exists() && `${slugSnap.data()?.workspaceId || ''}`.trim() !== workspaceId) {
        throw new Error('That workspace URL path is already taken.');
      }

      transaction.set(workspaceRef, {
        ...payload,
        createdAt: serverTimestamp()
      }, { merge: true });
      transaction.set(slugRef, {
        slug,
        workspaceId,
        userId: ownerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      if (customDomain) {
        transaction.set(getDomainRef(customDomain), {
          domain: customDomain,
          workspaceId,
          userId: ownerId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
      transaction.set(getMemberRef(workspaceId, ownerId), {
        userId: ownerId,
        role: 'owner',
        access: 'edit',
        invitedBy: ownerId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
    });

    return {
      id: workspaceId,
      ...payload,
      role: 'owner',
      access: 'edit',
      createdAt: new Date(),
      updatedAt: new Date()
    };
  },

  async ensureDefaultWorkspace(userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to load a default workspace');

    const snapshot = await getDocs(query(
      collection(db, WORKSPACES_COLLECTION),
      where('userId', '==', ownerId),
      orderBy('updatedAt', 'desc')
    ));

    if (!snapshot.empty) {
      return normalizeWorkspace(snapshot.docs[0]);
    }

    return this.createWorkspace({
      title: 'My Workspace',
      description: 'Default workspace for your sheets',
      defaultCollaboratorAccess: 'view',
      visibility: 'private'
    }, ownerId);
  },

  async getWorkspaceById(workspaceId) {
    let workspaceSnap;
    try {
      workspaceSnap = await getDoc(getWorkspaceRef(workspaceId));
    } catch (error) {
      throw withWorkspaceUpdateContext('reading the workspace document [trace: workspace-doc-read]', error);
    }
    if (!workspaceSnap.exists()) {
      throw new Error('Workspace not found');
    }

    return normalizeWorkspace(workspaceSnap);
  },

  async getWorkspaceBySlug(workspaceSlug) {
    const slug = normalizeWorkspaceSlug(workspaceSlug);
    const slugSnap = await getDoc(getSlugRef(slug));
    if (!slugSnap.exists()) {
      throw new Error('Workspace not found');
    }

    return this.getWorkspaceById(slugSnap.data().workspaceId);
  },

  async getWorkspaceByCustomDomain(domain) {
    const normalized = normalizeWorkspaceCustomDomain(domain);
    if (!normalized) {
      throw new Error('Workspace domain is required');
    }

    let domainSnap = null;
    try {
      domainSnap = await getDoc(getDomainRef(normalized));
    } catch (error) {
      if (!isPermissionDeniedError(error)) {
        throw withWorkspaceUpdateContext('reading the domain lookup [trace: domain-lookup-read]', error);
      }
    }
    if (domainSnap?.exists()) {
      return this.getWorkspaceById(domainSnap.data().workspaceId);
    }

    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('Workspace not found');
    }

    let ownedWorkspaceSnapshot;
    try {
      ownedWorkspaceSnapshot = await getDocs(query(
        collection(db, WORKSPACES_COLLECTION),
        where('customDomain', '==', normalized),
        where('userId', '==', currentUserId)
      ));
    } catch (error) {
      throw withWorkspaceUpdateContext('querying owned workspaces by custom domain [trace: owned-domain-query]', error);
    }

    if (!ownedWorkspaceSnapshot.empty) {
      const workspace = normalizeWorkspace(ownedWorkspaceSnapshot.docs[0]);

      try {
        await setDoc(getDomainRef(normalized), {
          domain: normalized,
          workspaceId: workspace.id,
          userId: workspace.userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      } catch (error) {
        if (!isPermissionDeniedError(error)) {
          throw withWorkspaceUpdateContext('backfilling the domain index [trace: domain-index-backfill]', error);
        }
      }

      return workspace;
    }

    let membershipSnapshot;
    try {
      membershipSnapshot = await getDocs(query(
        collectionGroup(db, WORKSPACE_MEMBERS_SUBCOLLECTION),
        where('userId', '==', currentUserId)
      ));
    } catch (error) {
      throw withWorkspaceUpdateContext('querying workspace memberships [trace: workspace-memberships-query]', error);
    }

    for (const memberSnap of membershipSnapshot.docs) {
      const workspaceRef = memberSnap.ref.parent.parent;
      if (!workspaceRef) continue;
      let workspaceSnap;
      try {
        workspaceSnap = await getDoc(workspaceRef);
      } catch (error) {
        throw withWorkspaceUpdateContext('reading a member workspace document [trace: member-workspace-read]', error);
      }
      if (!workspaceSnap.exists()) continue;
      const workspace = normalizeWorkspace(workspaceSnap);
      if (normalizeWorkspaceCustomDomain(workspace.customDomain) === normalized) {
        return workspace;
      }
    }

    throw new Error('Workspace not found');
  },

  async getUserWorkspaces(userId) {
    const ownerId = userId || auth.currentUser?.uid;
    if (!ownerId) throw new Error('User is required to load workspaces');

    const snapshot = await getDocs(query(
      collection(db, WORKSPACES_COLLECTION),
      where('userId', '==', ownerId),
      orderBy('updatedAt', 'desc')
    ));

    return snapshot.docs.map((item) => normalizeWorkspace(item));
  },

  async getSharedWorkspaces(userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) throw new Error('User is required to load shared workspaces');

    let membershipSnapshot;
    try {
      membershipSnapshot = await getDocs(query(
        collectionGroup(db, WORKSPACE_MEMBERS_SUBCOLLECTION),
        where('userId', '==', currentUserId)
      ));
    } catch (error) {
      if (isPermissionDeniedError(error)) return [];
      throw error;
    }

    const workspaces = await Promise.all(
      membershipSnapshot.docs.map(async (memberSnap) => {
        try {
          const workspaceRef = memberSnap.ref.parent.parent;
          if (!workspaceRef) return null;
          const workspaceSnap = await getDoc(workspaceRef);
          if (!workspaceSnap.exists()) return null;
          const workspace = normalizeWorkspace(workspaceSnap);
          if (workspace.userId === currentUserId) return null;
          return {
            ...workspace,
            memberRole: normalizeWorkspaceRole(memberSnap.data().role),
            memberAccess: normalizeWorkspaceAccess(memberSnap.data().access)
          };
        } catch {
          // Skip inaccessible or stale membership records so one bad document
          // does not block loading all shared workspaces.
          return null;
        }
      })
    );

    const userSnap = await getDoc(doc(db, USERS_COLLECTION, currentUserId)).catch(() => null);
    const organizationIds = normalizeStringList(
      userSnap?.exists?.() ? userSnap.data().approvedOrganizationIds : []
    );
    const organizationSnapshots = await Promise.all(organizationIds.map((organizationId) => (
      getDocs(query(
        collection(db, WORKSPACES_COLLECTION),
        where('organizationIds', 'array-contains', organizationId),
        orderBy('updatedAt', 'desc')
      )).catch(() => null)
    )));
    const organizationWorkspaces = organizationSnapshots
      .filter(Boolean)
      .flatMap((snapshot) => snapshot.docs.map((item) => ({
        ...normalizeWorkspace(item),
        memberRole: 'organization',
        memberAccess: 'view'
      })))
      .filter((workspace) => workspace.userId !== currentUserId);

    return sortByUpdatedAtDesc(
      [...workspaces.filter(Boolean), ...organizationWorkspaces].reduce((output, workspace) => {
        if (!workspace?.id || output.some((item) => item.id === workspace.id)) return output;
        output.push(workspace);
        return output;
      }, [])
    );
  },

  async getAccessibleWorkspaces(userId) {
    const currentUserId = userId || auth.currentUser?.uid;
    if (!currentUserId) throw new Error('User is required to load workspaces');

    const [owned, shared] = await Promise.all([
      this.getUserWorkspaces(currentUserId),
      this.getSharedWorkspaces(currentUserId).catch(() => [])
    ]);

    return sortByUpdatedAtDesc(
      [...owned, ...shared].reduce((acc, workspace) => {
        if (!workspace?.id || acc.some((item) => item.id === workspace.id)) return acc;
        acc.push(workspace);
        return acc;
      }, [])
    );
  },

  async getTemplateWorkspaces() {
    const snapshot = await getDocs(query(
      collection(db, WORKSPACES_COLLECTION),
      where('isTemplate', '==', true),
      orderBy('updatedAt', 'desc')
    ));

    return snapshot.docs.map((item) => normalizeWorkspace(item));
  },

  async getAdminAccessibleWorkspaces() {
    const snapshot = await getDocs(query(
      collection(db, WORKSPACES_COLLECTION),
      where('adminBuilderAccessEnabled', '==', true)
    ));

    return sortByUpdatedAtDesc(snapshot.docs.map((item) => normalizeWorkspace(item)));
  },

  async updateWorkspaceBasics(workspaceId, updates = {}) {
    const payload = stripUndefinedDeep({
      title: Object.prototype.hasOwnProperty.call(updates, 'title') ? `${updates.title || ''}`.trim() : undefined,
      description: Object.prototype.hasOwnProperty.call(updates, 'description') ? `${updates.description || ''}`.trim() : undefined,
      organizationIds: Object.prototype.hasOwnProperty.call(updates, 'organizationIds')
        ? normalizeStringList(updates.organizationIds)
        : undefined,
      visibility: Object.prototype.hasOwnProperty.call(updates, 'visibility') ? normalizeWorkspaceVisibility(updates.visibility) : undefined,
      defaultCollaboratorAccess: Object.prototype.hasOwnProperty.call(updates, 'defaultCollaboratorAccess')
        ? normalizeWorkspaceAccess(updates.defaultCollaboratorAccess)
        : undefined,
      allowCrossSheetReferences: Object.prototype.hasOwnProperty.call(updates, 'allowCrossSheetReferences')
        ? updates.allowCrossSheetReferences !== false
        : undefined,
      defaultLogSheetId: Object.prototype.hasOwnProperty.call(updates, 'defaultLogSheetId')
        ? (`${updates.defaultLogSheetId || ''}`.trim() || null)
        : undefined,
      adminBuilderAccessEnabled: Object.prototype.hasOwnProperty.call(updates, 'adminBuilderAccessEnabled')
        ? normalizeAdminBuilderAccessEnabled(updates.adminBuilderAccessEnabled)
        : undefined,
      logoUrl: Object.prototype.hasOwnProperty.call(updates, 'logoUrl')
        ? (`${updates.logoUrl || ''}`.trim() || null)
        : undefined,
      primaryColor: Object.prototype.hasOwnProperty.call(updates, 'primaryColor')
        ? (normalizeWorkspacePrimaryColor(updates.primaryColor) || DEFAULT_WORKSPACE_PRIMARY_COLOR)
        : undefined,
      aiConfig: Object.prototype.hasOwnProperty.call(updates, 'aiConfig')
        ? normalizeWorkspaceAiConfig(updates.aiConfig)
        : undefined,
      agentConfig: Object.prototype.hasOwnProperty.call(updates, 'agentConfig')
        ? normalizeWorkspaceAgentConfig(updates.agentConfig)
        : undefined,
      dashboardConfig: Object.prototype.hasOwnProperty.call(updates, 'dashboardConfig')
        ? sanitizeWorkspaceDashboardConfig(updates.dashboardConfig, updates.dashboardSheets || [], { seedLegacy: false })
        : undefined,
      isTemplate: Object.prototype.hasOwnProperty.call(updates, 'isTemplate')
        ? sanitizeTemplateFlag(updates.isTemplate)
        : undefined,
      templateClonedFromId: Object.prototype.hasOwnProperty.call(updates, 'templateClonedFromId')
        ? (`${updates.templateClonedFromId || ''}`.trim() || null)
        : undefined,
      updatedAt: serverTimestamp()
    });

    try {
      await updateDoc(getWorkspaceRef(workspaceId), payload);
    } catch (error) {
      throw withWorkspaceUpdateContext('updating the workspace [trace: workspace-doc]', error);
    }

    return payload;
  },

  async updateWorkspaceRouting(workspaceId, updates = {}) {
    let existingWorkspace;
    try {
      existingWorkspace = await this.getWorkspaceById(workspaceId);
    } catch (error) {
      throw withWorkspaceUpdateContext('loading the current workspace [trace: preflight-workspace]', error);
    }
    const existingSlug = `${existingWorkspace.slug || ''}`.trim();
    const previousCustomDomain = normalizeWorkspaceCustomDomain(existingWorkspace.customDomain);

    let nextSlug = existingSlug;
    if (Object.prototype.hasOwnProperty.call(updates, 'slug')) {
      try {
        nextSlug = await ensureUniqueWorkspaceSlug(updates.slug, workspaceId);
      } catch (error) {
        throw withWorkspaceUpdateContext('validating the workspace path [trace: preflight-slug]', error);
      }
    }

    let nextCustomDomain = previousCustomDomain;
    if (Object.prototype.hasOwnProperty.call(updates, 'customDomain')) {
      const requestedCustomDomain = normalizeWorkspaceCustomDomain(updates.customDomain);
      if (requestedCustomDomain !== previousCustomDomain) {
        try {
          nextCustomDomain = await ensureUniqueWorkspaceCustomDomain(updates.customDomain, workspaceId);
        } catch (error) {
          throw withWorkspaceUpdateContext('validating the workspace domain [trace: preflight-domain]', error);
        }
      }
    }

    const payload = stripUndefinedDeep({
      slug: Object.prototype.hasOwnProperty.call(updates, 'slug') ? (nextSlug || null) : undefined,
      customDomain: Object.prototype.hasOwnProperty.call(updates, 'customDomain') ? (nextCustomDomain || null) : undefined,
      updatedAt: serverTimestamp()
    });

    const slugChanged = Boolean(Object.prototype.hasOwnProperty.call(updates, 'slug') && nextSlug !== existingSlug);
    const domainChanged = Boolean(Object.prototype.hasOwnProperty.call(updates, 'customDomain') && nextCustomDomain !== previousCustomDomain);

    if (!slugChanged && !domainChanged) {
      return payload;
    }

    try {
      await runTransaction(db, async (transaction) => {
        transaction.update(getWorkspaceRef(workspaceId), payload);
        if (slugChanged) {
          transaction.set(getSlugRef(nextSlug), {
            slug: nextSlug,
            workspaceId,
            userId: existingWorkspace.userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }, { merge: true });
          if (existingSlug) {
            transaction.delete(getSlugRef(existingSlug));
          }
        }

        if (nextCustomDomain && domainChanged) {
          transaction.set(getDomainRef(nextCustomDomain), {
            domain: nextCustomDomain,
            workspaceId,
            userId: existingWorkspace.userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          }, { merge: true });
        }

        if (previousCustomDomain && previousCustomDomain !== nextCustomDomain) {
          transaction.delete(getDomainRef(previousCustomDomain));
        }
      });
    } catch (error) {
      if (slugChanged && domainChanged) {
        throw withWorkspaceUpdateContext('updating the workspace URL mappings [trace: mappings-both]', error);
      }
      if (slugChanged) {
        throw withWorkspaceUpdateContext('updating the workspace path [trace: mappings-slug]', error);
      }
      if (domainChanged) {
        throw withWorkspaceUpdateContext('updating the workspace domain [trace: mappings-domain]', error);
      }
      throw withWorkspaceUpdateContext('updating the workspace [trace: workspace-doc]', error);
    }

    return payload;
  },

  async updateWorkspaceCustomDomain(workspaceId, customDomain) {
    let existingWorkspace;
    try {
      existingWorkspace = await this.getWorkspaceById(workspaceId);
    } catch (error) {
      throw withWorkspaceUpdateContext('loading the current workspace [trace: preflight-workspace]', error);
    }

    const previousCustomDomain = normalizeWorkspaceCustomDomain(existingWorkspace.customDomain);
    const requestedCustomDomain = normalizeWorkspaceCustomDomain(customDomain);

    if (requestedCustomDomain === previousCustomDomain) {
      return {
        customDomain: requestedCustomDomain || null
      };
    }

    const nextCustomDomain = requestedCustomDomain;

    try {
      await updateDoc(getWorkspaceRef(workspaceId), {
        customDomain: nextCustomDomain || null,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      throw withWorkspaceUpdateContext('updating the workspace custom domain field [trace: workspace-domain-field]', error);
    }

    try {
      if (nextCustomDomain) {
        await setDoc(getDomainRef(nextCustomDomain), {
          domain: nextCustomDomain,
          workspaceId,
          userId: existingWorkspace.userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      if (previousCustomDomain && previousCustomDomain !== nextCustomDomain) {
        await deleteDoc(getDomainRef(previousCustomDomain));
      }
    } catch (error) {
      throw withWorkspaceUpdateContext('writing the domain index [trace: domain-index-write]', error);
    }

    return {
      customDomain: nextCustomDomain || null
    };
  },

  async ensureWorkspaceDomainIndex(workspaceId) {
    let workspace;
    try {
      workspace = await this.getWorkspaceById(workspaceId);
    } catch (error) {
      throw withWorkspaceUpdateContext('loading the workspace for domain index sync [trace: domain-index-ensure-load]', error);
    }

    const normalizedDomain = normalizeWorkspaceCustomDomain(workspace.customDomain);
    if (!normalizedDomain) {
      return null;
    }

    try {
      await setDoc(getDomainRef(normalizedDomain), {
        domain: normalizedDomain,
        workspaceId: workspace.id,
        userId: workspace.userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      throw withWorkspaceUpdateContext('ensuring the domain index [trace: domain-index-ensure-write]', error);
    }

    return {
      domain: normalizedDomain,
      workspaceId: workspace.id
    };
  },

  async updateWorkspace(workspaceId, updates = {}) {
    const basicUpdates = {};
    const routingUpdates = {};

    ['title', 'description', 'organizationIds', 'visibility', 'defaultCollaboratorAccess', 'allowCrossSheetReferences', 'defaultLogSheetId', 'logoUrl', 'primaryColor', 'aiConfig', 'agentConfig', 'isTemplate', 'templateClonedFromId'].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(updates, key)) {
        basicUpdates[key] = updates[key];
      }
    });
    ['slug', 'customDomain'].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(updates, key)) {
        routingUpdates[key] = updates[key];
      }
    });

    const response = {};
    if (Object.keys(basicUpdates).length) {
      Object.assign(response, await this.updateWorkspaceBasics(workspaceId, basicUpdates));
    }
    if (Object.keys(routingUpdates).length) {
      Object.assign(response, await this.updateWorkspaceRouting(workspaceId, routingUpdates));
    }
    return response;
  },

  async addMember(workspaceId, memberData = {}, userId) {
    const actingUserId = userId || auth.currentUser?.uid;
    if (!actingUserId) throw new Error('User is required to add workspace members');

    const targetUserId = `${memberData.userId || ''}`.trim();
    if (!targetUserId) throw new Error('Member userId is required');

    await assertNotWorkspaceOwnerMember(workspaceId, targetUserId);

    await setDoc(getMemberRef(workspaceId, targetUserId), {
      userId: targetUserId,
      role: normalizeWorkspaceRole(memberData.role),
      access: normalizeWorkspaceAccess(memberData.access),
      email: `${memberData.email || ''}`.trim().toLowerCase() || undefined,
      displayName: `${memberData.displayName || ''}`.trim() || undefined,
      invitedBy: actingUserId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
  },

  async getWorkspaceMembers(workspaceId) {
    const snapshot = await getDocs(query(
      getMembersCollectionRef(workspaceId),
      orderBy('createdAt', 'asc')
    ));

    return snapshot.docs.map((item) => ({
      id: item.id,
      ...item.data()
    }));
  },

  async getWorkspaceMember(workspaceId, userId) {
    const memberUserId = userId || auth.currentUser?.uid;
    if (!memberUserId) return null;
    const snapshot = await getDoc(getMemberRef(workspaceId, memberUserId));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  },

  async removeMember(workspaceId, memberUserId) {
    await assertNotWorkspaceOwnerMember(workspaceId, memberUserId);
    await deleteDoc(getMemberRef(workspaceId, memberUserId));
  }
};
